import { Locales } from '@griply/types';
import { useEffect, useRef } from 'react';
import { useToday } from 'shared/contexts/today';
import { useCreatePushTokenMutation } from 'shared/hooks/queries/use-create-push-token-mutation';
import { usePushNotificationMessageQuery } from 'shared/hooks/queries/use-push-notification-message-query';
import { useNotificationPermission } from 'shared/hooks/use-notification-permission';
import { useUser } from 'shared/hooks/use-user';
import { fetchFreshPushToken } from 'shared/services/backend-api';
import { getErrorReporter } from 'shared/services/error-tracking';
import { getPushNotificationTokenDate } from 'shared/services/local-storage';
import { PushNotificationMessageRequestState } from 'shared/services/local-storage/push-notification-message';

export const useDailyPushNotificationCheck = () => {
  const today = useToday();
  const { mutateAsync: createToken } = useCreatePushTokenMutation();
  const user = useUser();
  const { data: messageState } = usePushNotificationMessageQuery(user?.uid);
  const [permission] = useNotificationPermission();
  const hasRequested = useRef(false);

  useEffect(() => {
    if (
      !user?.uid ||
      messageState !== PushNotificationMessageRequestState.Asked ||
      permission !== 'granted'
    ) {
      return;
    }

    // no stored date means never registered here or deliberately opted out — never re-enroll
    const lastTokenDate = getPushNotificationTokenDate(user.uid);
    if (!lastTokenDate) {
      return;
    }

    const daysSinceLastToken = Math.floor(
      (today.getTime() - lastTokenDate.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (daysSinceLastToken <= 200 || hasRequested.current) {
      return;
    }
    hasRequested.current = true;

    const refreshToken = async () => {
      const token = await fetchFreshPushToken();
      if (!token) {
        return;
      }
      await createToken({
        userId: user.uid,
        token,
        locale: user.settings?.language ?? Locales.EnUS,
      });
    };

    refreshToken().catch((error) =>
      getErrorReporter().captureException(error, {
        tags: { source: 'daily-push-notification-check' },
      }),
    );
  }, [
    user?.uid,
    user?.settings?.language,
    createToken,
    messageState,
    permission,
    today,
  ]);
};
